import React, { useState, useEffect } from "react";
import { useStateValue } from "../../../State";
import "../combinedplan.css";

import ContentBoxMUI from "../../../Components/ContentBoxMUI";
import Languages from "../../../translations";
import ChildRow from "../../Vasu/ChildRow";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Edit from "@mui/icons-material/EditRounded";
import Check from "@mui/icons-material/Check";
import Undo from "@mui/icons-material/Undo";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MobileStepper from "@mui/material/MobileStepper";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import SwipeableViews from "react-swipeable-views";
import SnackbarComponent from "../../../Components/Snackbar";
import { getCombinedPlanSectionTitle } from "../sectionTitles";

import {
  containsMoreThanEmptySpaces,
  getProfilePictureData,
} from "../../../utils";

const BG_COLOR = "#FBDCCE";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("fi-FI") +
    " " +
    d.toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" })
  );
};

const SpringEvaluationPart = (props) => {
  const { state, actions } = useStateValue();
  const combinedPlanData = state.combinedPlan.data;
  const SpringEvaluation = combinedPlanData
    ? combinedPlanData.SpringEvaluation
    : null;
  const planId = combinedPlanData?.PlanId;
  const isLocked = combinedPlanData?.Locked === true;
  const isReadOnly = isLocked;
  const saveState = state.combinedPlan?.saveState;
  const isSaving = saveState === "SAVING_COMBINED_PLAN_SPRING_TEXT";
  const savedOk = saveState === "COMBINED_PLAN_SPRING_TEXT_SAVED_OK";
  const savedFailed = saveState === "COMBINED_PLAN_SPRING_TEXT_SAVED_FAILED";

  const { history, match } = props;
  const childId = match.params.ChildId;
  const child = state.children?.ChildList?.find((c) => c.Id === childId);
  const childpicture = getProfilePictureData(state, child?.Id);
  const childFirstName = child?.FirstName ?? null;
  const childLastName = child?.LastName ?? null;
  const childColor = child?.BgColor ?? null;
  const sectionTitle = getCombinedPlanSectionTitle(
    combinedPlanData,
    "springEvaluation",
    combinedPlanData?.SpringEvaluationSectionTitle,
    "Kevään arviointi",
  );

  const [slideIndex, setSlideIndex] = useState(-1);
  const [editing, setEditing] = useState(false);
  const [editableText, setEditableText] = useState("");
  const [showSaveBtn, setShowSaveBtn] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  useEffect(() => {
    if (!combinedPlanData) {
      history.replace(`/combinedplan/${childId}`);
    }
  }, []);

  useEffect(() => {
    if (savedOk) {
      setShowSnackbar(true);
      setSnackbarText(Languages.formSaved);
    } else if (savedFailed) {
      setShowSnackbar(true);
      setSnackbarText(Languages.requestFailed);
    }
  }, [saveState]);

  if (!combinedPlanData || !SpringEvaluation) {
    return <div className="CombinedPlanContainer"></div>;
  }

  const { IndependentSection, TeacherSummarySection } = SpringEvaluation;
  const teacherSections = TeacherSummarySection?.Sections ?? [];
  const guardianSection =
    teacherSections.find((s) => s.SubSectionLevel2 === 524) ?? null;

  // Build slides array: IndependentSection + TeacherSummary sections
  const slides = [];

  if (IndependentSection) {
    slides.push({
      label: IndependentSection.Label || Languages.leopsIHaveLearnedInPreschool,
      staffText: IndependentSection.StaffText || null,
      staffTextAuthor: IndependentSection.StaffTextAuthor || null,
      staffTextSaved: IndependentSection.StaffTextSaved || null,
      otherEntries: IndependentSection.OtherEntries || [],
      editable: true,
      editableText: IndependentSection.Text,
      textAuthor: IndependentSection.TextAuthor || null,
      textSaved: IndependentSection.TextSaved || null,
      subSectionLevel1: IndependentSection.SubSectionLevel1,
      subSectionLevel2: IndependentSection.SubSectionLevel2,
    });
  }

  if (TeacherSummarySection) {
    teacherSections.forEach((section) => {
      const isGuardianEditable = section.SubSectionLevel2 === 524; // SprintEvalSubtitle2GuardianGreetings
      slides.push({
        label: section.Label,
        staffText: isGuardianEditable
          ? section.StaffText || null
          : section.Text || null,
        staffTextAuthor: isGuardianEditable
          ? section.StaffTextAuthor || null
          : section.StaffTextAuthor || null,
        staffTextSaved: isGuardianEditable
          ? section.StaffTextSaved || null
          : section.StaffTextSaved || null,
        otherEntries: section.OtherEntries || [],
        editable: isGuardianEditable,
        editableText: isGuardianEditable ? section.Text : null,
        textAuthor: isGuardianEditable ? section.TextAuthor || null : null,
        textSaved: isGuardianEditable ? section.TextSaved || null : null,
        subSectionLevel1: section.SubSectionLevel1,
        subSectionLevel2: section.SubSectionLevel2,
      });
    });
  }

  const maxSlideCount = slides.length;

  // Sync editable text state when slide changes or data updates
  useEffect(() => {
    if (slideIndex >= 0 && slideIndex < slides.length) {
      const slide = slides[slideIndex];
      if (slide.editable) {
        setEditableText(slide.editableText ?? "");
      }
      setEditing(false);
      setShowSaveBtn(false);
    }
  }, [slideIndex, IndependentSection?.Text, guardianSection?.Text]);

  const handlePrev = () => {
    if (editing) return;
    if (slideIndex > 0) setSlideIndex(slideIndex - 1);
  };

  const handleNext = () => {
    if (editing) return;
    if (slideIndex < maxSlideCount - 1) setSlideIndex(slideIndex + 1);
  };

  const handleChangeIndex = (index) => {
    if (!editing) setSlideIndex(index);
  };

  const handleMenuClick = (index) => {
    setSlideIndex(index);
    // Enable browser back to return to menu
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = () => {
      window.history.pushState(null, "", window.location.href);
      setSlideIndex(-1);
      window.onpopstate = undefined;
      window.history.back();
    };
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    const slide = slides[slideIndex];
    setEditableText(val);
    setShowSaveBtn(
      containsMoreThanEmptySpaces(val) ||
        (slide?.editableText && slide.editableText !== ""),
    );
  };

  const handleSave = () => {
    const slide = slides[slideIndex];
    actions.triggerSaveCombinedPlanSpringText({
      planId,
      text: editableText,
      subSectionLevel1: slide.subSectionLevel1,
      subSectionLevel2: slide.subSectionLevel2,
    });
    setEditing(false);
    setShowSaveBtn(false);
  };

  const handleCancel = () => {
    const slide = slides[slideIndex];
    setEditableText(slide?.editableText ?? "");
    setShowSaveBtn(false);
    setEditing(false);
  };

  const renderEditableField = (slide) => {
    return (
      <Box
        sx={{
          backgroundColor: BG_COLOR,
          borderRadius: 2,
          boxShadow: 2,
          mt: 2,
          pt: 2,
          pb: 2,
          px: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            ml: 1,
            mr: 1,
            mb: 0.5,
          }}
        >
          <Typography
            color="textSecondary"
            sx={{
              fontWeight: "bold",
              fontSize: 14,
              color: "#424242",
            }}
          >
            {Languages.observations}
          </Typography>
          {slide.textSaved && (
            <Typography variant="caption" color="text.secondary">
              {slide.textSaved}
            </Typography>
          )}
        </Box>

        <TextField
          multiline
          minRows={3}
          maxRows={7}
          fullWidth
          disabled={isReadOnly || !editing || isSaving}
          value={editableText}
          onChange={handleTextChange}
          onKeyDown={(e) => {
            if (
              e.keyCode === 13 &&
              editableText.split(/\r\n|\r|\n/).length >= 15
            ) {
              e.preventDefault();
            }
          }}
          inputProps={{ maxLength: 2400, style: { color: "black" } }}
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "black",
            },
          }}
          InputProps={{
            endAdornment: !isReadOnly ? (
              <InputAdornment position="start">
                {isSaving ? (
                  <CircularProgress
                    size={20}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  />
                ) : !editing ? (
                  <IconButton
                    sx={{ position: "absolute", top: 5, right: 5 }}
                    onClick={() => setEditing(true)}
                    size="medium"
                  >
                    <Edit />
                  </IconButton>
                ) : (
                  <IconButton
                    sx={{ position: "absolute", top: 5, right: 5 }}
                    onClick={handleCancel}
                    size="medium"
                  >
                    <Undo />
                  </IconButton>
                )}
                {showSaveBtn && editing && !isSaving && (
                  <IconButton
                    sx={{ position: "absolute", top: 5, right: 45 }}
                    onClick={handleSave}
                    size="medium"
                  >
                    <Check />
                  </IconButton>
                )}
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#424242",
                    position: "absolute",
                    bottom: 5,
                    right: 10,
                  }}
                >
                  {editableText.length}/2400
                </Typography>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
    );
  };

  const renderReadonlyBox = (text, author, saved) => {
    if (!text) return null;
    return (
      <Box
        sx={{
          backgroundColor: BG_COLOR,
          borderRadius: 2,
          boxShadow: 1,
          mt: 1,
          pt: 1.5,
          pb: 1.5,
          px: 1,
        }}
      >
        {(author || saved) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
              ml: 1,
              mr: 1,
            }}
          >
            <Typography
              sx={{ fontWeight: "bold", fontSize: 13, color: "#424242" }}
            >
              {author || ""}
            </Typography>
            {saved && (
              <Typography variant="caption" color="text.secondary">
                {saved}
              </Typography>
            )}
          </Box>
        )}
        <Typography
          variant="body1"
          sx={{
            color: "black",
            mb: 2,
            ml: 1,
            whiteSpace: "pre-line",
            overflowWrap: "break-word",
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const renderSlideContent = (slide) => {
    return (
      <Box className="col-12">
        {/* Staff/read-only text with chip divider */}
        {slide.staffText != null && (
          <>
            <Divider textAlign="left">
              <Chip
                label={
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: 14,
                      color: "#424242",
                    }}
                  >
                    {Languages.StaffNotes}
                  </Typography>
                }
                variant="outlined"
              />
            </Divider>
            {renderReadonlyBox(
              slide.staffText,
              slide.staffTextAuthor,
              slide.staffTextSaved,
            )}
          </>
        )}

        {/* Other guardians' entries */}
        {slide.otherEntries && slide.otherEntries.length > 0 && (
          <>
            <Divider textAlign="left" sx={{ mt: 2 }}>
              <Chip
                label={
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: 14,
                      color: "#424242",
                    }}
                  >
                    {Languages.OtherNotes}
                  </Typography>
                }
                variant="outlined"
              />
            </Divider>
            {slide.otherEntries.map((entry, idx) =>
              renderReadonlyBox(
                entry.Text,
                entry.PersonName,
                formatDate(entry.Saved),
              ),
            )}
          </>
        )}

        {/* For editable sections, show "Omat havainnot" chip divider + editable field */}
        {slide.editable && (
          <>
            <Divider textAlign="left" sx={{ mt: 2 }}>
              <Chip
                label={
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: 14,
                      color: "#424242",
                    }}
                  >
                    {Languages.OwnNotes}
                  </Typography>
                }
                variant="outlined"
              />
            </Divider>
            {renderEditableField(slide)}
          </>
        )}
      </Box>
    );
  };

  // Determine the title for the stepper header
  const getSlideGroupTitle = () => {
    if (slideIndex === 0 && IndependentSection) {
      return IndependentSection.Label || Languages.leopsIHaveLearnedInPreschool;
    }
    return TeacherSummarySection?.Label || "Opettajan yhteenveto";
  };

  return (
    <div className="CombinedPlanContainer">
      <ContentBoxMUI>
        <Box sx={{ mt: 1 }}>
          <ChildRow
            childpicture={childpicture}
            childFirstName={childFirstName}
            childLastName={childLastName}
            childColor={childColor}
            FormId={combinedPlanData.PlanId}
            history={history}
          />
        </Box>

        <h4 className="textCenter">{sectionTitle}</h4>

        {slideIndex === -1 ? (
          /* ── Menu listing all sections ── */
          <div>
            {slides.map((slide, index) => (
              <List className="col-12" sx={{ mt: -1 }} key={index}>
                <ListItemButton
                  className="boxed"
                  sx={{
                    backgroundColor: BG_COLOR,
                    borderRadius: 0.5,
                    mt: 1.5,
                    boxShadow: 2,
                  }}
                  onClick={() => handleMenuClick(index)}
                >
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: "Bold",
                      fontSize: 16,
                      ml: 2,
                    }}
                    primary={slide.label}
                  />
                  <ListItemIcon>
                    <ArrowForwardIosIcon fontWeight="Bold" fontSize="small" />
                  </ListItemIcon>
                </ListItemButton>
              </List>
            ))}
          </div>
        ) : (
          /* ── Slide detail view with prev/next navigation ── */
          <Box>
            <SwipeableViews
              className={"col-12 textCenter slide pt-3"}
              index={slideIndex}
              key={slideIndex}
              onChangeIndex={handleChangeIndex}
              disabled={editing}
            >
              {slides.map((slide, index) => (
                <div key={index}>
                  <Box
                    className="br-top col-12"
                    sx={{ backgroundColor: BG_COLOR }}
                  >
                    <Typography
                      color="textSecondary"
                      sx={{
                        textAlign: "center",
                        fontWeight: "Bold",
                        fontSize: 18,
                        color: "#424242",
                        pt: 1,
                      }}
                    >
                      {getSlideGroupTitle()}
                    </Typography>
                    <MobileStepper
                      style={{
                        backgroundColor: BG_COLOR,
                        color: "black",
                        padding: 0,
                      }}
                      variant="text"
                      steps={maxSlideCount}
                      position="static"
                      activeStep={slideIndex}
                      nextButton={
                        <Button
                          size="small"
                          style={{
                            color: "black",
                            opacity:
                              slideIndex === maxSlideCount - 1 ? "0.5" : "",
                          }}
                          onClick={handleNext}
                          disabled={slideIndex === maxSlideCount - 1 || editing}
                        >
                          {Languages.next} <KeyboardArrowRight />
                        </Button>
                      }
                      backButton={
                        <Button
                          size="small"
                          style={{
                            color: "black",
                            opacity: slideIndex === 0 ? "0.5" : "",
                          }}
                          onClick={handlePrev}
                          disabled={slideIndex === 0 || editing}
                        >
                          <KeyboardArrowLeft /> {Languages.prev}
                        </Button>
                      }
                    />
                  </Box>
                  <Box
                    className="br-btm col-12"
                    sx={{
                      boxShadow: 2,
                      mb: 3,
                      pb: 1,
                      backgroundColor: "white",
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      sx={{
                        textAlign: "center",
                        fontWeight: "Medium",
                        fontSize: 16,
                        color: "#424242",
                        pt: 1,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        px: 1,
                      }}
                    >
                      {slide.label}
                    </Typography>
                  </Box>
                </div>
              ))}
            </SwipeableViews>

            {renderSlideContent(slides[slideIndex])}
          </Box>
        )}

        {showSnackbar && (
          <SnackbarComponent
            verticalPosition={"bottom"}
            show={showSnackbar}
            okColor={savedOk}
            message={snackbarText}
            callback={() => setShowSnackbar(false)}
          />
        )}
      </ContentBoxMUI>
    </div>
  );
};

export default SpringEvaluationPart;
